import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import MiniControl from 'creative-ui/uikit/MiniControl';
import Modal from 'creative-ui/uikit/Modal';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Captcha from '@/components/Captcha';
import { useCaptcha } from '@/components/Captcha/useCaptcha';
import { Flex } from '@/components/Layout/Flex/Flex';
import { Text } from '@/components/Typography/Text/Text';
import { useSignupPasswordManager } from '@/screens/signup-password/hooks/useSignupPasswordManager';

interface TermsAndConditionsFormProps {
  onAccept: (captchaValue: string | undefined) => void;
  onCancel?: () => void;
  isSubmitting: boolean;
  privacyUrl: string;
  fieldErrors: Record<string, string>;
  texts?: {
    privacyLinkText?: string;
    checkboxLabel?: string;
    buttonText?: string;
    cancelLinkText?: string;
  };
}

interface FormData {
  tos: boolean;
  captcha: string;
}

function TermsAndConditionsForm({
  onAccept,
  onCancel,
  isSubmitting,
  privacyUrl,
  fieldErrors,
  texts,
}: Readonly<TermsAndConditionsFormProps>) {
  const { t } = useTranslation(['signupPassword', 'common']);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: { tos: false, captcha: '' },
  });

  const { captcha, isCaptchaAvailable } = useSignupPasswordManager();

  const { captchaConfig, captchaProps, captchaValue } = useCaptcha(
    captcha || undefined,
  );

  // SDK first, i18n fallback
  const privacyLinkText =
    texts?.privacyLinkText || t('termsAndConditions.privacyLinkText');
  const checkboxLabel =
    texts?.checkboxLabel || t('termsAndConditions.checkboxLabel');
  const buttonText = texts?.buttonText || t('termsAndConditions.buttonText');
  const cancelLinkText =
    texts?.cancelLinkText || t('termsAndConditions.cancelLinkText');
  const submittingText = t('form.submittingText');
  const checkboxRequiredError = t('termsAndConditions.checkboxRequired');
  const cancelDialogTitle = t('termsAndConditions.cancelDialog.title');
  const cancelDialogDescription = t(
    'termsAndConditions.cancelDialog.description',
  );
  const keepAccountButtonText = t(
    'termsAndConditions.cancelDialog.keepAccountButtonText',
  );
  const deleteAccountButtonText = t(
    'termsAndConditions.cancelDialog.deleteAccountButtonText',
  );

  const onSubmit = () => {
    onAccept(captchaValue);
  };

  const handleConfirmCancel = () => {
    setIsCancelDialogOpen(false);
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gapY="24px">
          <CtaContainer alignment="left">
            <Cta
              type="ghost-inline"
              onClick={() => window.open(privacyUrl, '_blank')}>
              {privacyLinkText}
            </Cta>
          </CtaContainer>

          <Controller
            name="tos"
            control={control}
            rules={{
              required: checkboxRequiredError,
            }}
            render={({ field }) => (
              <MiniControl
                {...field}
                checked={field.value}
                status={errors.tos ? 'error' : undefined}
                infoMessage={errors.tos?.message}>
                {checkboxLabel}
              </MiniControl>
            )}
          />

          {isCaptchaAvailable && captchaConfig && (
            <Captcha
              captcha={captchaConfig}
              name="captcha"
              control={control}
              setError={setError}
              clearErrors={clearErrors}
              error={fieldErrors.captcha}
              rules={{ required: t('common:captcha.errorRequired') }}
              {...captchaProps}
            />
          )}
          <CtaContainer stack>
            <Cta
              className="c-submitCta"
              type="primary"
              submit
              isWaiting={isSubmitting}
              disabled={isSubmitting}>
              {isSubmitting ? submittingText : buttonText}
            </Cta>
            {onCancel && (
              <Cta type="ghost" onClick={() => setIsCancelDialogOpen(true)}>
                {cancelLinkText}
              </Cta>
            )}
          </CtaContainer>
        </Flex>
      </form>

      {onCancel && (
        <Modal
          open={isCancelDialogOpen}
          onClickClose={() => setIsCancelDialogOpen(false)}
          title={cancelDialogTitle}>
          <Text align="center" className="u-margin-b-l">
            {cancelDialogDescription}
          </Text>
          <CtaContainer stack>
            <Cta type="secondary" onClick={() => setIsCancelDialogOpen(false)}>
              {keepAccountButtonText}
            </Cta>
            <Cta type="primary" theme="danger" onClick={handleConfirmCancel}>
              {deleteAccountButtonText}
            </Cta>
          </CtaContainer>
        </Modal>
      )}
    </>
  );
}

export default TermsAndConditionsForm;
